/* MyTomette · le ciel de votre rencontre — le studio guidé de la fiche produit (Lot F-ciel, 15/10/2026).
 *
 * COPIE ADAPTÉE DE mt-carte-studio.js (fiche carte, inchangée) : même squelette (révélation progressive au
 * bureau, guide plein écran au téléphone, caméra des mots, brouillon, lien d'état, pont commerce), étapes
 * propres au ciel (cahier §6.2, K28) : Lieu → Moment → Format → Visuel → Vos mots → Prête.
 *  - Ordinateur : aperçu collant à gauche, étapes à droite (faites en noir avec leur résumé et « Modifier »).
 *  - Téléphone : guide plein écran, une décision par écran ; les corps des étapes du bureau y sont DÉPLACÉS
 *    (marqueurs <!--ciel-guide-position-->), puis remis en place : jamais de formulaire en double.
 *
 * Qui fait quoi :
 *  - window.CarteLieu (carte, Lot R) : recherche (IGN et Photon à la frappe, Nominatim sur Entrée seulement),
 *    coordonnées collées sans réseau, libellé gravable (D35). Pas d'épingle en V1 (cahier §4.3, critique M6).
 *  - window.CielTemps (Lot R-ciel) : fuseau du lieu, heure locale → UTC (heures inexistantes et ambiguës), libellé.
 *  - window.CielRendu (Lot R-ciel) : scène et résumé, aperçu au canvas (immédiat), GRAVURE (le SVG même qui
 *    partira au laser), gabarit du cartouche (C1 de la carte), formats.
 *  - window.CartePeintre (carte) : l'aperçu sur la vraie photo de la pièce.
 *  - mt-ciel-produit.js (ce lot, chargé à l'étape Moment) : le panier, par le moteur expert caché.
 *
 * L'heure : on ne manipule JAMAIS une date « locale de l'appareil » (cahier §1.3) ; la saisie est du texte
 * (« 2019-02-14 », « 22:00 ») converti par CielTemps.versUTC dans le fuseau DU LIEU.
 *
 * L'état tient en moins de 1 Ko : brouillon (localStorage, 7 jours, sous try/catch) et lien #ciel=<base64url>
 * (fragment : rien ne part au serveur). Le ciel est déterministe : il se redessine à l'identique ailleurs.
 */
(() => {
  'use strict';
  const CHANNEL = 'mt-ciel-product';
  const PIECES = {hexa: {W: 120, H: 100}, carree: {W: 130, H: 130}};
  const CORPS_MIN = 3.0;                                           // D10 de la carte : aucun texte du gabarit sous 3,0 mm de corps
  const TEINTE = '#6f5331';                                        // la teinte de la gravure dans l'aperçu (moteur, peintre)
  const VISUELS = ['medaillon', 'medaillon-nuit', 'pleine'];
  /* Visuel par défaut (décision de Benjamin, 30/10/2026) : le client part de la PLEINE tomette, il peut toujours choisir le
     médaillon ensuite. Une création déjà faite (lien #ciel=, « Modifier » du panier, brouillon) rouvre avec SON visuel, lu dans
     le lien (champ vi) ; un lien sans visuel lisible garde le défaut d'avant (médaillon), jamais la pleine. Le préréglage
     produit (métachamp mytomette.ciel_preset, absent au 30/10) passe toujours avant. */
  const VISUEL_DEFAUT = 'pleine', VISUEL_AVANT = 'medaillon';
  const DATES = ['points-heure', 'long-heure', 'points', 'long', 'libre', 'sans'];
  const COORDS = ['dms', 'dec', 'sans'];
  const BROUILLON_JOURS = 7;

  /* base64url d'une chaîne UTF-8, et retour */
  function versB64(s) {
    const b = new TextEncoder().encode(s); let bin = '';
    for (let i = 0; i < b.length; i++) bin += String.fromCharCode(b[i]);
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  function depuisB64(s) {
    const bin = atob(String(s).replace(/-/g, '+').replace(/_/g, '/'));
    return new TextDecoder('utf-8', {fatal: true}).decode(Uint8Array.from(bin, c => c.charCodeAt(0)));
  }
  const borne = (v, a, b) => Math.max(a, Math.min(b, v));
  const r5 = v => Math.round(v * 1e5) / 1e5;
  const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : 'mtci' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10));
  const reduit = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const echapper = s => String(s == null ? '' : s).replace(/[&<>"]/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;'}[c]));

  /* COPIE DE carte/mise.js (CarteRendu.mise, elle-même COPIE DE mtLayoutText et usableWidthFrac du moteur) :
     le moteur RÉDUIT un texte trop large ; l'aperçu et les boîtes de réserve font exactement pareil.
     Copiée ici pour ne pas charger tout mt-carte-rendu.js sur la fiche du ciel (cahier §6.1 : 45 Ko visés). */
  const mise = (() => {
    const SIZE_REF = 12, MT_LH = 1.12, MT_LAY_W = 1000;
    const CM = {hexa: {wcm: 12, hcm: 10, sq: false}, carree: {wcm: 13, hcm: 13, sq: true}};
    const POLICES_CSS = {dmsans: "'DM Sans',sans-serif", Inter: "'Inter',sans-serif"};
    function usableWidthFrac(x, y, forme) {
      x = x != null ? x : 0.5; y = y != null ? y : 0.5;
      const edge = 2 * Math.min(x, 1 - x);
      if ((CM[forme] || CM.hexa).sq) return Math.max(0.2, Math.min(0.97, edge * 0.985));
      const hexW = 1 - Math.abs(y - 0.5);
      return Math.max(0.2, Math.min(hexW * 0.96, edge * 0.96));
    }
    let cx = null;
    return function (el, forme, o = {}) {
      forme = PIECES[forme] ? forme : 'hexa';
      const sh = CM[forme], P = PIECES[forme];
      if (!cx) cx = document.createElement('canvas').getContext('2d');
      const txt0 = String(el.text == null ? '' : el.text), txt = el.font === 'dmcaps' || el.smallcaps ? txt0.toUpperCase() : txt0;
      const x = el.x != null ? el.x : 0.5, y = el.y != null ? el.y : 0.5, size = +el.size || 6.5, ls = +el.ls || 0, weight = el.weight || 400;
      const fam = (o.css && o.css(el.font, weight)) || POLICES_CSS[el.font] || "'DM Sans',sans-serif";
      const minSize = o.minSize != null ? o.minSize : Math.max(2, size * 0.5);
      const W = MT_LAY_W, H = W * (sh.hcm / sh.wcm);
      let s = size, lines = [txt], blockH = 0, wFrac = usableWidthFrac(x, y, forme), wMax = 0, fpx = 0;
      for (let pass = 0; pass < 4; pass++) {
        fpx = (s * (SIZE_REF / sh.wcm)) / 100 * W;
        cx.font = weight + ' ' + fpx + 'px ' + fam;
        try { cx.letterSpacing = (ls ? ls * fpx : 0) + 'px'; } catch (_) { /* navigateur ancien */ }
        const halfH = ((blockH || fpx) * 0.5) / H;
        const yEdge = Math.max(0, Math.min(1, y + (y >= 0.5 ? halfH : -halfH)));
        wFrac = usableWidthFrac(x, yEdge, forme);
        const maxW = wFrac * W;
        lines = txt.split('\n'); wMax = 0;
        for (const l of lines) { const w = cx.measureText(l).width; if (w > wMax) wMax = w; }
        blockH = lines.length * fpx * MT_LH;
        const availH = Math.max(0.15, 2 * Math.min(y, 1 - y) * 0.96) * H;
        const k = Math.min(wMax > 0 ? maxW / wMax : 1, blockH > 0 ? availH / blockH : 1);
        if (k >= 0.995) break;
        const ns = Math.max(minSize, s * k);
        if (ns >= s - 0.01) { s = ns; break; }
        s = ns;
      }
      try { cx.letterSpacing = '0px'; } catch (_) { /* idem */ }
      const fpxFinal = (s * (SIZE_REF / sh.wcm)) / 100 * W, kmm = P.W / W;
      cx.font = weight + ' ' + fpxFinal + 'px ' + fam;
      try { cx.letterSpacing = (ls ? ls * fpxFinal : 0) + 'px'; } catch (_) { /* idem */ }
      let wFin = 0; for (const l of lines) { const w = cx.measureText(l).width; if (w > wFin) wFin = w; }
      try { cx.letterSpacing = '0px'; } catch (_) { /* idem */ }
      const bw = wFin * kmm, bh = lines.length * fpxFinal * MT_LH * kmm;
      return {lines, size: Math.round(s * 100) / 100, wFrac, boite: {x: x * P.W - bw / 2, y: y * P.H - bh / 2, w: bw, h: bh}};
    };
  })();

  function mount(root) {
    if (root.dataset.cielStudio) return;
    root.dataset.cielStudio = 'true';
    const config = JSON.parse(root.querySelector('[data-ciel-config]').textContent);
    const en = /^en\b/i.test(config.locale || '');
    const esp = !en && /^es\b/i.test(config.locale || '');
    const loc = en ? 'en' : esp ? 'es' : 'fr';
    const T_ES = esp ? {
      "cet endroit": "este lugar",
      "Choisissez une date entre 1900 et 2050": "Elige una fecha entre 1900 y 2050",
      "Ce fuseau horaire est inconnu de votre navigateur : choisissez-en un autre.": "Tu navegador no conoce esta zona horaria: elige otra.",
      "Cette date ou cette heure est illisible.": "Esta fecha o esta hora no se puede leer.",
      "tomette": "tomette",
      "carreau": "baldosa cuadrada",
      "Aperçu de votre ": "Vista previa de tu ",
      " : le ciel du 14 février 2025 à 22 h au-dessus du Vieux-Port, Marseille": ": el cielo del 14 de febrero de 2025 a las 22:00 sobre el Vieux-Port de Marsella",
      "(exemple)": "(ejemplo)",
      " : le ciel du ": ": el cielo del ",
      " au-dessus de ": " sobre ",
      "(en cours de gravure)": "(grabándose)",
      "Exemple · Vieux-Port, Marseille · 14.02.2025": "Ejemplo · Vieux-Port, Marsella · 14.02.2025",
      "Votre ciel apparaîtra avec la date": "Tu cielo aparecerá con la fecha",
      "Votre ciel se grave…": "Grabando tu cielo…",
      "Le catalogue des étoiles ne répond pas. Réessayez dans un instant.": "El catálogo de estrellas no responde. Inténtalo de nuevo en un momento.",
      "Votre ciel n’a pas pu être préparé. Réessayez dans un instant.": "No se ha podido preparar tu cielo. Inténtalo de nuevo en un momento.",
      "Pour trouver votre lieu, votre recherche est envoyée à des services cartographiques publics (IGN, Photon/komoot, OpenStreetMap). La date et l’heure servent uniquement à calculer votre ciel. Rien n’est enregistré chez nous avant l’ajout au panier.": "Para encontrar tu lugar, tu búsqueda se envía a servicios cartográficos públicos (IGN, Photon/komoot, OpenStreetMap). La fecha y la hora solo sirven para calcular tu cielo. No guardamos nada antes de que lo añadas a la cesta.",
      "Aucun lieu trouvé. Essayez la ville, ou collez des coordonnées.": "No se ha encontrado ningún lugar. Prueba con la ciudad o pega unas coordenadas.",
      "Écrivez le nom en lettres latines": "Escribe el nombre en caracteres latinos",
      "— Choisir —": "— Elegir —",
      "Recherche du fuseau horaire du lieu…": "Buscando la zona horaria del lugar…",
      "Dans quel fuseau était-ce ? Choisissez-le ci-dessous.": "¿En qué zona horaria era? Elígela abajo.",
      "Fuseau : ": "Zona horaria: ",
      "✓ Le ciel du ": "✓ El cielo del ",
      "Oui, le ciel du ": "Sí, el cielo del ",
      " à ": " a las ",
      "✓ Le ciel de ": "✓ El cielo de las ",
      "Oui, le ciel de ": "Sí, el cielo de las ",
      "Ce jour-là au-dessus de vous : ": "Ese día sobre ti: ",
      "Cette nuit-là au-dessus de vous : ": "Esa noche sobre ti: ",
      "Ce soir-là au-dessus de vous : ": "Esa noche sobre ti: ",
      " (sans heure)": " (sin hora)",
      " : saisissez-le ci-dessous.": ": escríbelo abajo.",
      "Écrivez le nom du lieu en lettres latines : certains caractères ne peuvent pas être gravés.": "Escribe el nombre del lugar en caracteres latinos: algunos caracteres no se pueden grabar.",
      "Votre texte contient des caractères qui ne peuvent pas être gravés.": "Tu texto contiene caracteres que no se pueden grabar.",
      "Le nom du lieu": "El nombre del lugar",
      "Votre texte": "Tu texto",
      "Les coordonnées": "Las coordenadas",
      " est trop long pour être gravé lisiblement : raccourcissez-le.": " es demasiado largo para grabarse de forma legible: acórtalo.",
      "Le médaillon": "El medallón",
      "Le médaillon nuit": "El medallón noche",
      "Plein carreau": "Baldosa cuadrada completa",
      "Pleine tomette": "Tomette completa",
      "Tomette": "Tomette hexagonal",
      "Carreau": "Baldosa cuadrada",
      " (heure inconnue)": " (hora desconocida)",
      " percée": " con agujero",
      "Au-dessus de vous ce jour-là : ": "Sobre ti ese día: ",
      "Au-dessus de vous cette nuit-là : ": "Sobre ti esa noche: ",
      "Au-dessus de vous ce soir-là : ": "Sobre ti esa noche: ",
      ", le ciel jusqu’aux bords": ", el cielo hasta los bordes",
      "Il manque le moment : la date et l’heure, à l’étape « Moment ».": "Falta el momento: la fecha y la hora, en el paso «Momento».",
      "Où étiez-vous ?": "¿Dónde estabais?",
      "Quand ?": "¿Cuándo?",
      "Tomette ou carreau ?": "¿Tomette o baldosa cuadrada?",
      "Votre ciel": "Tu cielo",
      "Les mots de votre ciel": "Las palabras de tu cielo",
      "Il est prêt.": "Ya está listo.",
      "C’est ici": "Es aquí",
      "Suivant": "Siguiente",
      "Personnaliser mes mots": "Personalizar mis palabras",
      "Voir mon ciel": "Ver mi cielo",
      "← Retour": "← Volver",
      "Fermer": "Cerrar",
      "Terminer →": "Terminar →",
      "Suivant →": "Siguiente →",
      "Le panier ne répond pas. Réessayez dans un instant.": "La cesta no responde. Inténtalo de nuevo en un momento.",
      "Le panier tarde à répondre. Réessayez dans un instant.": "La cesta tarda en responder. Inténtalo de nuevo en un momento.",
      "Vous modifiez le ciel de votre panier : l’ajout remplacera l’ancienne version.": "Estás modificando el cielo de tu cesta: al añadirlo se sustituirá la versión anterior.",
      "Vérification de votre ciel…": "Comprobando tu cielo…",
      "Préparation du fichier de gravure…": "Preparando el archivo de grabado…",
      "Enregistrement de votre ciel…": "Guardando tu cielo…",
      "Ajout au panier…": "Añadiendo a la cesta…",
      "Vérification du panier…": "Comprobando la cesta…",
      "Votre ciel est dans le panier.": "Tu cielo está en la cesta.",
      "L’ajout n’a pas abouti.": "No se ha podido añadir.",
      "Choisissez d’abord votre lieu.": "Primero elige tu lugar.",
      "Votre ciel n’a pas pu être préparé.": "No se ha podido preparar tu cielo.",
      "Votre ciel se redessine pour cette forme. Réessayez dans un instant.": "Tu cielo se está redibujando para esta forma. Inténtalo de nuevo en un momento.",
      "Lien copié ✓": "Enlace copiado ✓",
      "La création de votre ciel est momentanément indisponible. Réessayez dans quelques minutes.": "La creación de tu cielo no está disponible por ahora. Inténtalo de nuevo en unos minutos.",
      "Ce lien de ciel est incomplet : recommencez à partir de votre lieu.": "Este enlace de cielo está incompleto: vuelve a empezar desde tu lugar.",
    } : null;
    const t = (fr, anglais) => en ? anglais : esp ? (T_ES[fr] ?? fr) : fr;
    const $ = s => root.querySelector(s), $$ = s => [...root.querySelectorAll(s)];
    const R = () => window.CielRendu, T = () => window.CielTemps, L = () => window.CarteLieu;
    const peintre = window.CartePeintre ? window.CartePeintre.create() : null;
    const preset = config.preset && typeof config.preset === 'object' ? config.preset : {};
    const mobile = matchMedia('(max-width:880px)');
    const statut = $('[data-ciel-status]');
    const guide = root.querySelector('[data-ciel-guide]');
    const scene = $('[data-ciel-scene]'), piece = $('[data-ciel-piece]');
    const toile = $('[data-ciel-peinture]'), calque = $('[data-ciel-apercu]');
    const prix = new Intl.NumberFormat(config.locale || 'fr', {style: 'currency', currency: config.currency || 'EUR'});
    const variantes = new Map((config.variants || []).map(v => [v.key, v]));

    /* ------------------------------------------------------------------
       L'état (cahier §6.3)
       ------------------------------------------------------------------ */
    function etatInitial() {
      return {
        v: 1, lieu: null,
        moment: {date: '', heure: '', inconnue: false, zone: '', zoneSource: '', choix: 1, accepte: false},
        forme: 'hexa', trou: false,
        visuel: VISUELS.includes(preset.visuel) ? preset.visuel : VISUEL_DEFAUT,
        textes: {fmtDate: DATES.includes(preset.fmtDate) ? preset.fmtDate : 'points-heure', libre: '', lieuTexte: '', lieuAuto: true, fmtCoords: COORDS.includes(preset.fmtCoords) ? preset.fmtCoords : 'dms'},
        cadeau: {on: false, msg: ''}
      };
    }
    let etat = etatInitial();
    let etape = 0, faites = new Set(), ciel = null, ins = null, sceneCiel = null, erreurTemps = '', erreurCiel = '', geo = null, catalogue = false;
    let jetonGravure = 0, jetonZone = 0, gravureEnCours = null, recherche = null, zoneEnCours = false;
    let exemple = {hexa: null, carree: null}, sauverMinuteur = 0, graverMinuteur = 0, peindreMinuteur = 0;
    let editionPanier = null, alerteLatin = '', fuseauOuvert = false, visuelExemple = VISUEL_DEFAUT;

    const dims = () => PIECES[etat.forme];
    const varianteDe = (forme, trou) => variantes.get(forme + '|' + (trou ? '1' : '0'));
    const prixDe = (forme, trou) => { const v = varianteDe(forme, trou); return v ? (typeof v.prix === 'string' && v.prix ? v.prix : prix.format(v.priceCents / 100)) : ''; };
    const tailleFond = f => Math.max(PIECES[f].W, PIECES[f].H) / 1.2;
    /* Le nom du lieu dans les phrases de l'heure (« 22 h 00 à Marseille ») : la commune, sinon le nom, sinon le libellé
       gravé remis en casse de titre (coordonnées collées), sinon « cet endroit » (jamais « à  : »). */
    const casseTitre = s => String(s || '').toLocaleLowerCase(loc).replace(/(^|[\s'’(-])(\p{L})/gu, (_, a, b) => a + b.toLocaleUpperCase(loc));
    const nomVille = () => etat.lieu ? (etat.lieu.commune || etat.lieu.nom || casseTitre(etat.textes.lieuTexte) || t('cet endroit', 'this place')) : '';

    /* ------------------------------------------------------------------
       L'instant : la saisie (heure DU LIEU) → UTC, par CielTemps (cahier §1.3, contrats CHANGEMENTS 8)
       ------------------------------------------------------------------ */
    function calculerInstant() {
      ins = null; erreurTemps = ''; sceneCiel = null;
      const m = etat.moment;
      if (!etat.lieu || !T() || !/^\d{4}-\d{2}-\d{2}$/.test(m.date)) return null;
      const bornes = (config.bornes && config.bornes.min) ? config.bornes : T().BORNES;
      if (m.date < bornes.min || m.date > bornes.max) { erreurTemps = R() ? R().texte('bornes', loc) : t('Choisissez une date entre 1900 et 2050', 'Choose a date between 1900 and 2050'); return null; }
      const h = m.inconnue ? (config.heureInconnue || '22:00') : m.heure;
      if (!/^\d{2}:\d{2}$/.test(h) || !m.zone) return null;
      let r;
      try { r = T().versUTC({date: m.date, heure: h}, m.zone); }
      catch (e) {
        const c = e && e.code;
        erreurTemps = c === 'BORNES' ? (R() ? R().texte('bornes', loc) : '') : c === 'ZONE' ? t('Ce fuseau horaire est inconnu de votre navigateur : choisissez-en un autre.', 'Your browser does not know this time zone: choose another one.') : t('Cette date ou cette heure est illisible.', 'This date or time cannot be read.');
        return null;
      }
      let utc = r.utc, local = r.local, heure = m.inconnue ? 'inconnue' : 'connue', saisie = '';
      let aff = r;                                  // ce que dit la ligne du fuseau : le décalage RETENU
      if (r.statut === 'ambigue') { const i = m.choix === 2 && r.choix[1] ? 1 : 0, c = r.choix[i]; utc = c.utc; aff = {...r, utc: c.utc, decalage: c.decalage, ete: c.ete}; if (!m.inconnue) heure = 'ambigue-' + (i + 1); }
      else if (r.statut === 'inexistante') { local = r.proposition.date + 'T' + r.proposition.heure; if (!m.inconnue) { heure = 'decalee'; saisie = h; } }
      ins = {r, aff, utc, local, zone: m.zone, heure, saisie, pret: r.statut !== 'inexistante' || m.accepte || m.inconnue};
      if (catalogue && R()) { try { sceneCiel = R().scene(params()); } catch { sceneCiel = null; } }
      return ins;
    }
    const pretAGraver = () => !!(etat.lieu && ins && ins.pret && R());
    /* « ce soir-là », « cette nuit-là » ou « ce jour-là » : la scène de jour d'abord (soleil levé), puis l'heure
       retenue (0 h à 5 h 59 : la nuit) ; heure inconnue = le ciel de 22 h, donc le soir. */
    function quandCiel() {
      if (sceneCiel && sceneCiel.jour) return 'jour';
      if (!ins || ins.heure === 'inconnue') return 'soir';
      const h = +ins.local.slice(11, 13);
      return h < 6 ? 'nuit' : 'soir';
    }

    /* Les textes du gabarit, tels qu'ils seront gravés (contrats §5.3) */
    function fmtEffectif() {
      const f = etat.textes.fmtDate;
      if (ins && ins.heure === 'inconnue') return f === 'points-heure' ? 'points' : f === 'long-heure' ? 'long' : f;   // K12 : jamais d'heure inventée
      return f;
    }
    function texteDate() {
      if (!R()) return '';
      const f = fmtEffectif();
      if (f === 'libre') return R().formats.instant(etat.textes.libre, 'libre', loc);
      if (!ins) return '';
      return R().formats.instant(ins.heure === 'inconnue' ? {date: ins.local.slice(0, 10)} : ins.local, f, loc);
    }
    const texteCoords = () => (etat.lieu && R()) ? R().formats.coords(etat.lieu.lat, etat.lieu.lon, etat.textes.fmtCoords, config.locale) : '';
    function textesGabarit() {
      const o = {v: 1, date: texteDate(), iso: ins ? ins.local.slice(0, 10) : etat.moment.date, heure: ins && ins.heure !== 'inconnue' ? ins.local.slice(11, 16) : '',
        fmtDate: fmtEffectif(), lieu: etat.textes.lieuTexte.trim(), coords: texteCoords(), fmtCoords: etat.textes.fmtCoords};
      if (ins && ins.heure === 'decalee' && ins.saisie) o.saisie = ins.saisie;   // jamais gravée (CHANGEMENTS 8)
      return o;
    }
    const cssPolice = id => window.CartePeintre ? window.CartePeintre.cssFamille(id) : '';
    /* Le cartouche (format recette, CielRendu.elementsGabarit), tailles ajustées EXACTEMENT comme le moteur les
       réduira (mise, ci-dessus) : l'aperçu ne ment pas et les réserves couvrent le texte. */
    function elementsTextes(valeurs, forme = etat.forme, visuel = etat.visuel) {
      const v = valeurs || {lieu: etat.textes.lieuTexte.trim(), date: texteDate(), coords: texteCoords()};
      return R().elementsGabarit(forme, visuel, v).map(e => {
        if (e.type !== 'text') return e;
        const m = mise(e, forme, {css: cssPolice, minSize: CORPS_MIN / 1.2});
        return {...e, size: m.size, __mise: m};
      });
    }
    /* Les boîtes de réserve, au format de C().mtFondBoites() (COPIE DE boitesDe de la fiche carte). */
    function boitesDe(els) {
      const {W, H} = dims();
      return els.map(e => {
        if (e.type === 'text') { const b = e.__mise.boite; return {x: b.x, y: b.y, w: b.w, h: b.h, type: 'text', texte: e.text, gabarit: true}; }
        const l = e.size * 1.2;
        return {x: e.x * W - l / 2, y: e.y * H - 1, w: l, h: 2, type: 'logo', cle: 'custom_filet', gabarit: true};
      });
    }
    function params(textes, visuel = etat.visuel) {
      return {lieu: {lat: etat.lieu.lat, lon: etat.lieu.lon}, instant: {utc: ins.utc, local: ins.local, zone: ins.zone, heure: ins.heure},
        visuel, forme: etat.forme, trou: etat.trou, langue: loc, textes: textes || textesGabarit()};
    }
    /* La clé géométrique d'un ciel : ce qui change les étoiles (pas les mots). */
    const cleCiel = () => (etat.lieu && ins) ? JSON.stringify([r5(etat.lieu.lat), r5(etat.lieu.lon), ins.utc, etat.visuel, etat.forme, etat.trou, loc]) : '';
    const cielAccorde = () => !!(ciel && ciel.cle === cleCiel() && new RegExp('\\sdata-mtci-forme="' + etat.forme + '"').test(String(ciel.svg).slice(0, 1600)));
    /* La recette : le ciel (fond, contrats §5.1), puis lieu, date, filet, coordonnées (K18). Les clés __role sont des
       indications pour le pont : elles ne partent jamais au panier. */
    function recette(els) {
      const elements = (els || elementsTextes()).map(e => { const c = {...e, __role: e._mtci}; delete c.__mise; delete c._mtci; return c; });
      return {v: 1, shape: etat.forme, hole: etat.trou,
        elements: [{type: 'logo', logo: 'custom_ciel', x: .5, y: .5, size: Math.round(tailleFond(etat.forme) * 1e4) / 1e4, lock: true, color: '#4a2616', svg: ciel.svg}, ...elements]};
    }

    /* ------------------------------------------------------------------
       Le lien #ciel= et le brouillon (le message cadeau n'entre jamais dans un lien)
       ------------------------------------------------------------------ */
    function encoder() {
      if (!etat.lieu) return '';
      const l = etat.lieu, m = etat.moment, x = etat.textes;
      const o = {v: 1, l: [r5(l.lat), r5(l.lon)], n: (l.nom || '').slice(0, 80), cm: (l.commune || '').slice(0, 60), p: (l.pays || '').slice(0, 40), so: l.source || '',
        m: [m.date, m.heure, m.inconnue ? 1 : 0, m.zone, m.choix === 2 ? 2 : 1, m.accepte ? 1 : 0], z: m.zoneSource || '',
        f: etat.forme, t: etat.trou ? 1 : 0, vi: etat.visuel, d: [x.fmtDate, x.libre.slice(0, 40)], lt: x.lieuTexte.slice(0, 32), la: x.lieuAuto ? 1 : 0, k: x.fmtCoords};
      return versB64(JSON.stringify(o));
    }
    /* Lecture stricte : un lien abîmé ou trafiqué ne pose jamais de valeur farfelue. */
    function decoder(s) {
      if (typeof s !== 'string' || !/^[A-Za-z0-9_-]{8,2000}$/.test(s)) return null;
      let o; try { o = JSON.parse(depuisB64(s)); } catch { return null; }
      if (!o || o.v !== 1 || !Array.isArray(o.l)) return null;
      const lat = +o.l[0], lon = +o.l[1];
      if (!Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
      const x = etatInitial(), txt = (v, n) => typeof v === 'string' ? v.replace(/[\u0000-\u001f<>]/g, '').slice(0, n) : '';
      x.lieu = {lat, lon, nom: txt(o.n, 80), commune: txt(o.cm, 60), pays: txt(o.p, 40), source: ['ign', 'photon', 'nominatim', 'coller'].includes(o.so) ? o.so : ''};
      if (Array.isArray(o.m)) {
        const zone = txt(o.m[3], 64);
        x.moment = {date: /^\d{4}-\d{2}-\d{2}$/.test(o.m[0]) ? o.m[0] : '', heure: /^\d{2}:\d{2}$/.test(o.m[1]) ? o.m[1] : '', inconnue: o.m[2] === 1,
          zone: T() && zone && T().zoneValide(zone) ? zone : '', zoneSource: ['pays', 'table', 'client'].includes(o.z) ? o.z : '', choix: o.m[4] === 2 ? 2 : 1, accepte: o.m[5] === 1};
      }
      if (o.f === 'hexa' || o.f === 'carree') x.forme = o.f;
      x.trou = o.t === 1;
      x.visuel = VISUELS.includes(o.vi) ? o.vi : VISUELS.includes(preset.visuel) ? preset.visuel : VISUEL_AVANT;   // jamais forcé en pleine (30/10)
      if (Array.isArray(o.d)) { x.textes.fmtDate = DATES.includes(o.d[0]) ? o.d[0] : 'points-heure'; x.textes.libre = txt(o.d[1], 40); }
      x.textes.lieuTexte = txt(o.lt, 32).toLocaleUpperCase(loc); x.textes.lieuAuto = o.la === 1;
      if (COORDS.includes(o.k)) x.textes.fmtCoords = o.k;
      return x;
    }
    const cleBrouillon = 'mt-ciel-brouillon:' + config.id;
    function sauver() {
      clearTimeout(sauverMinuteur);
      sauverMinuteur = setTimeout(() => {
        if (!etat.lieu) return;
        try { localStorage.setItem(cleBrouillon, JSON.stringify({t: Date.now(), s: encoder(), etape, cadeau: etat.cadeau})); } catch { /* navigation privée : l'état reste en mémoire */ }
      }, 400);
    }
    function lireBrouillon() {
      try {
        const b = JSON.parse(localStorage.getItem(cleBrouillon) || 'null');
        if (!b || Date.now() - b.t > BROUILLON_JOURS * 864e5) return null;
        const x = decoder(b.s); if (!x) return null;
        if (b.cadeau && typeof b.cadeau === 'object') x.cadeau = {on: b.cadeau.on === true, msg: typeof b.cadeau.msg === 'string' ? b.cadeau.msg.slice(0, 200) : ''};
        return {etat: x, etape: borne(+b.etape || 0, 0, 5)};
      } catch { return null; }
    }
    function lienCiel() {
      const u = new URL(config.url, location.origin); u.search = ''; u.hash = 'ciel=' + encoder();
      return u.href;
    }
    /* Le duo (cahier §5.2, V1) : la fiche carte, préremplie par SON lien #carte= (aucune retouche de la carte). */
    function lienCarte() {
      const base = typeof config.carteUrl === 'string' && /^\/[a-z0-9\/-]+$/i.test(config.carteUrl) ? config.carteUrl : '';
      if (!base || !etat.lieu) return '';
      const o = {v: 1, l: [r5(etat.lieu.lat), r5(etat.lieu.lon)], n: (etat.lieu.nom || '').slice(0, 80), cm: (etat.lieu.commune || '').slice(0, 60), f: etat.forme, t: 0,
        d: [ins ? ins.local.slice(0, 10) : etat.moment.date, 'points', ''], lt: etat.textes.lieuTexte.slice(0, 32), la: etat.textes.lieuAuto ? 1 : 0, k: etat.textes.fmtCoords};
      return base + '#carte=' + versB64(JSON.stringify(o));
    }

    /* ------------------------------------------------------------------
       L'aperçu
       ------------------------------------------------------------------ */
    function dire(texte, erreur = false) { if (statut) { statut.textContent = texte || ''; statut.dataset.error = String(!!erreur); } }
    function attente(oui, texte) {
      const a = $('[data-ciel-attente]'); if (!a) return;
      if (texte) $('[data-ciel-attente-texte]').textContent = texte;
      a.hidden = !oui;
    }
    async function demo(forme) {
      if (exemple[forme]) return exemple[forme];
      const url = forme === 'carree' ? config.assets.demoCarree : config.assets.demoHexa;
      const r = await fetch(url, {credentials: 'omit'}); if (!r.ok) throw Error('exemple indisponible');
      const svg = await r.text();
      if (!/^\s*<svg\b[^>]*\sdata-mt-fond="ciel"/.test(svg)) throw Error('exemple invalide');
      exemple[forme] = svg; return svg;
    }
    const sansMise = els => els.map(e => { const c = {...e}; delete c.__mise; delete c._mtci; return c; });
    /* La recette que l'on peint : le ciel du client, ou l'exemple du Vieux-Port tant qu'aucun lieu n'est choisi. */
    async function recettePeinte() {
      if (cielAccorde()) return recette();
      // un lieu est choisi : la pièce et ses mots, sans ciel (jamais l'exemple d'un autre lieu) ; l'aperçu au canvas s'y pose
      if (etat.lieu) return {v: 1, shape: etat.forme, hole: etat.trou, elements: sansMise(elementsTextes())};
      const forme = etat.forme, svg = await demo(forme), m = R().marques(svg), tx = (m && m.textes) || {};
      // les mots se posent sur le gabarit DU VISUEL de l'exemple (pleine depuis le 30/10), lu dans ses marques
      const vis = m && VISUELS.includes(m.visuel) ? m.visuel : VISUEL_AVANT; visuelExemple = vis;
      const els = sansMise(elementsTextes({lieu: tx.lieu || '', date: tx.date || '', coords: tx.coords || ''}, forme, vis));
      return {v: 1, shape: forme, hole: etat.trou, elements: [{type: 'logo', logo: 'custom_ciel', x: .5, y: .5, size: tailleFond(forme), lock: true, svg}, ...els]};
    }
    const taillePiece = () => ({w: Math.max(1, Math.round(piece.clientWidth)), h: Math.max(1, Math.round(piece.clientHeight))});
    const dpr = () => Math.min(2, window.devicePixelRatio || 1);
    let peinture = Promise.resolve();
    function peindre() {
      if (!peintre || !R()) return peinture;
      peinture = peinture.then(async () => {
        const {w, h} = taillePiece();
        if (w < 20) return;
        try {
          const rec = await recettePeinte();
          const res = await peintre.render(toile, rec, {width: w, height: h, dpr: dpr()});
          if (res.stale) return;
          geo = res; root.dataset.peint = 'true';
          dessinerApercu(); majAria(); camera();
        } catch (err) {
          if (!ciel) root.dataset.peint = 'false';
          console.warn('[ciel] aperçu', err);
        }
      });
      return peinture;
    }
    function peindreBientot(ms = 60) { clearTimeout(peindreMinuteur); peindreMinuteur = setTimeout(peindre, ms); }
    /* L'aperçu au canvas (CielRendu.apercu, quelques ms) posé sur la face de la pièce, en multiplication, tant que
       la gravure du ciel n'est pas arrivée (cahier §1.11 : jamais d'écran vide, jamais d'écran d'attente plein). */
    let horsEcran = null;
    function dessinerApercu() {
      const voir = !!(geo && ins && catalogue && R() && !cielAccorde() && etat.lieu);
      if (!voir) { calque.hidden = true; return; }
      const d = dpr(), {w, h} = taillePiece(), f = geo.face;
      if (calque.width !== Math.round(w * d) || calque.height !== Math.round(h * d)) { calque.width = Math.round(w * d); calque.height = Math.round(h * d); }
      horsEcran = horsEcran || document.createElement('canvas');
      horsEcran.width = Math.max(1, Math.round(f.width * d)); horsEcran.height = Math.max(1, Math.round(f.height * d));
      let ok = false;
      try { ok = R().apercu(horsEcran, params(), {teinte: TEINTE, piece: false}); } catch { ok = false; }
      const ctx = calque.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, calque.width, calque.height);
      if (!ok) { calque.hidden = true; return; }
      ctx.drawImage(horsEcran, f.x * d, f.y * d, f.width * d, f.height * d);
      calque.hidden = false;
    }
    function majAria() {
      const forme = etat.forme === 'hexa' ? t('tomette', 'tomette') : t('carreau', 'square tile');
      const exempleVu = !etat.lieu;
      const parts = [];
      if (exempleVu) parts.push(t('Aperçu de votre ', 'Preview of your ') + forme + t(' : le ciel du 14 février 2025 à 22 h au-dessus du Vieux-Port, Marseille', ': the sky of 14 February 2025 at 22:00 above the Vieux-Port, Marseille') + ', ' + nomVisuel(visuelExemple).toLowerCase() + ' ' + t('(exemple)', '(example)'));
      else {
        const quand = ins ? T().texteDate(ins.local.slice(0, 10), loc) + (ins.heure === 'inconnue' ? '' : (en ? ' at ' : esp ? ' a las ' : ' à ') + T().texteHeure(ins.local.slice(11, 16), loc)) : '';
        parts.push(t('Aperçu de votre ', 'Preview of your ') + forme + (quand ? t(' : le ciel du ', ': the sky of ') + quand : '') + t(' au-dessus de ', ' above ') + (etat.textes.lieuTexte || nomCourt()));
        parts.push(nomVisuel(etat.visuel).toLowerCase());
        if (sceneCiel) parts.push(sceneCiel.resume[loc]);
        if (ins && !cielAccorde()) parts.push(t('(en cours de gravure)', '(being engraved)'));
      }
      scene.setAttribute('aria-label', parts.filter(Boolean).join(', '));
      $('[data-ciel-badge]').textContent = exempleVu ? t('Exemple · Vieux-Port, Marseille · 14.02.2025', 'Example · Vieux-Port, Marseille · 14.02.2025') : (!ins ? t('Votre ciel apparaîtra avec la date', 'Your sky appears once you add the date') : '');
    }

    /* ------------------------------------------------------------------
       Gravure (worker du Lot R-ciel)
       ------------------------------------------------------------------ */
    // graverMinuteur vaut 0 dès qu'aucune saisie n'attend sa gravure (ajouter() s'y fie).
    function graverBientot(ms = 450) { clearTimeout(graverMinuteur); graverMinuteur = setTimeout(() => { graverMinuteur = 0; graver(); }, ms); }
    function graver() {
      clearTimeout(graverMinuteur); graverMinuteur = 0;
      if (!pretAGraver()) return Promise.resolve(null);
      const n = ++jetonGravure, els = elementsTextes(), textes = textesGabarit(), p = params(textes), cle = cleCiel();
      if (!cielAccorde()) dessinerApercu();
      attente(true, t('Votre ciel se grave…', 'Engraving your sky…'));
      const job = R().graver(p, boitesDe(els)).then(async res => {
        if (n !== jetonGravure) return null;
        ciel = {svg: res.svg, cle, encre: res.encre_mm2, textes: JSON.stringify(textes)}; erreurCiel = ''; root.dataset.ciel = 'true';
        await peindre();
        majTout();
        return res;
      }).catch(err => {
        if (n !== jetonGravure) return null;
        erreurCiel = messageErreur(err);
        console.warn('[ciel] gravure', err);
        majTout();
        return null;
      }).finally(() => { if (n === jetonGravure) { attente(false); gravureEnCours = null; } });
      gravureEnCours = job;
      return job;
    }
    function messageErreur(err) {
      const code = err && (err.code || err.message);
      if (code === 'BORNES' || code === 'date') return R().texte('bornes', loc);
      if (code === 'CATALOGUE' || code === 'catalogue') return t('Le catalogue des étoiles ne répond pas. Réessayez dans un instant.', 'The star catalogue is not responding. Please try again in a moment.');
      return t('Votre ciel n’a pas pu être préparé. Réessayez dans un instant.', 'Your sky could not be prepared. Please try again in a moment.');
    }
    /* Le catalogue sur le fil principal (scène, résumé, aperçu) : au premier lieu ou au premier moment. */
    function chargerCatalogue() {
      if (catalogue || !R()) return Promise.resolve(catalogue);
      return R().charger().then(() => { catalogue = true; calculerInstant(); majTout(); peindreBientot(0); if (etape === 3) vignettes(); return true; }).catch(err => { erreurCiel = messageErreur(err); majTout(); return false; });
    }
    /* Toute saisie qui change l'instant : recalcul, aperçu immédiat, gravure un peu plus tard. */
    function apresMoment(ms = 350) {
      calculerInstant();
      dessinerApercu(); majTout(); peindreBientot(0);
      if (etape === 3) vignettes();
      if (pretAGraver()) graverBientot(ms); else { clearTimeout(graverMinuteur); graverMinuteur = 0; }
      sauver();
    }

    /* ------------------------------------------------------------------
       Étape 1 — le lieu
       ------------------------------------------------------------------ */
    function textesLieu() {
      const out = {}, Tx = (L() && L().textes) || {};
      for (const k of Object.keys(Tx)) out[k] = en ? Tx[k][2] : esp && window.MT_CONFIG_I18N ? window.MT_CONFIG_I18N.text(Tx[k][0], Tx[k][1]) : Tx[k][1];
      out.vie = t('Pour trouver votre lieu, votre recherche est envoyée à des services cartographiques publics (IGN, Photon/komoot, OpenStreetMap). La date et l’heure servent uniquement à calculer votre ciel. Rien n’est enregistré chez nous avant l’ajout au panier.',
        'To find your place, your search is sent to public mapping services (IGN, Photon/komoot, OpenStreetMap). The date and time are only used to calculate your sky. Nothing is stored by us before you add it to your basket.');
      out.aucun = t('Aucun lieu trouvé. Essayez la ville, ou collez des coordonnées.', 'No place found. Try the town, or paste coordinates.');
      return out;
    }
    const nomCourt = () => etat.lieu ? [etat.lieu.nom, etat.lieu.commune && etat.lieu.commune !== etat.lieu.nom ? etat.lieu.commune : ''].filter(Boolean).join(', ') || texteCoordsBrut() : '';
    const texteCoordsBrut = () => etat.lieu && R() ? R().formats.coords(etat.lieu.lat, etat.lieu.lon, 'dms', config.locale) : '';
    function monterLieu() {
      const hote = $('[data-ciel-recherche]');
      if (!L()) return;
      L().config({donnees: {geo: config.geo}, geo: config.geo, locale: config.locale});
      const attenteInput = hote.querySelector('[data-ciel-recherche-attente]');
      const valeur = attenteInput ? attenteInput.value : '', avaitFocus = attenteInput && document.activeElement === attenteInput;
      recherche = L().monterRecherche(hote, {locale: config.locale, textes: textesLieu(), valeur, onChoix: choisirLieu, onErreur: () => {}});
      if (avaitFocus) recherche.focus();
      recherche.input.addEventListener('focus', () => { if (R()) R().prechauffer(); }, {once: true});
      /* Palliatif WebKit (COPIE DE la fiche carte) : un appui sur une option après Entrée peut arriver sans click. */
      const liste = hote.querySelector('.mtcx-lieu-liste');
      if (liste) {
        let clicVu = false;
        liste.addEventListener('click', () => { clicVu = true; }, true);
        liste.addEventListener('touchend', ev => {
          const li = ev.target.closest && ev.target.closest('[data-i]'); if (!li) return;
          clicVu = false;
          setTimeout(() => { if (!clicVu && li.isConnected && !liste.hidden) li.click(); }, 350);
        }, {passive: true});
      }
      if (valeur) recherche.chercher(valeur);
    }
    function etiqueter(lieu) {
      if (!etat.textes.lieuAuto || !L()) return;
      L().libelleGravable(lieu, {locale: config.locale}).then(r => {
        if (!etat.textes.lieuAuto) return;
        etat.textes.lieuTexte = r.texte || '';
        alerteLatin = r.ok ? '' : (r.message || t('Écrivez le nom en lettres latines', 'Write the name in Latin letters'));
        majChamps(); peindreBientot(); if (pretAGraver()) graverBientot(300); sauver();
      }).catch(() => {});
    }
    /* Le fuseau du lieu (K11) : France métropolitaine → Europe/Paris ; ailleurs, la table embarquée ; sinon on demande. */
    function trouverZone() {
      if (!etat.lieu || !T()) return;
      const n = ++jetonZone; zoneEnCours = true; majMoment();
      T().zoneDe({lat: etat.lieu.lat, lon: etat.lieu.lon, pays: etat.lieu.pays, source: etat.lieu.source}).then(z => {
        if (n !== jetonZone) return;
        zoneEnCours = false;
        if (etat.moment.zoneSource === 'client' && etat.moment.zone) { majMoment(); return; }   // le client a déjà corrigé : on ne l'écrase pas
        etat.moment.zone = z.zone || ''; etat.moment.zoneSource = z.zone ? z.source : '';
        if (!z.zone) fuseauOuvert = true;
        apresMoment(0);
      });
    }
    function choisirLieu(lieu) {
      if (!lieu || !Number.isFinite(+lieu.lat) || !Number.isFinite(+lieu.lon)) return;
      const avant = etat.lieu;
      etat.lieu = {lat: +lieu.lat, lon: +lieu.lon, nom: lieu.nom || '', commune: lieu.commune || '', pays: lieu.pays || '', source: lieu.source || ''};
      if (!avant || Math.abs(avant.lat - etat.lieu.lat) > 1e-4 || Math.abs(avant.lon - etat.lieu.lon) > 1e-4) { etat.moment.zone = ''; etat.moment.zoneSource = ''; fuseauOuvert = false; }
      root.dataset.lieu = 'true'; root.dataset.cree = 'true'; root.dataset.vue = 'ciel';
      $('[data-ciel-choisi]').hidden = false;
      // le libellé du lieu précédent ne survit jamais au changement de lieu : libellé sans réseau d'abord, gravable ensuite
      if (etat.textes.lieuAuto) { try { etat.textes.lieuTexte = (L() && L().libelle(etat.lieu, {locale: config.locale})) || ''; } catch { etat.textes.lieuTexte = ''; } }
      etiqueter(etat.lieu);
      chargerCatalogue(); if (R()) R().prechauffer();
      trouverZone();
      calculerInstant(); majTout(); peindre(); sauver();
      // l'option choisie quitte le DOM : le focus ne doit pas tomber sur <body> (COPIE DE la carte)
      requestAnimationFrame(() => {
        const a = document.activeElement;
        if (a && a !== document.body && a.isConnected && a.getClientRects().length && (root.contains(a) || (guide && guide.contains(a)))) return;
        const titre = guide && guide.open ? $('[data-ciel-guide-titre]') : $('[data-ciel-etape="0"] .mt-carte-etape__titre');
        if (titre) { titre.setAttribute('tabindex', '-1'); titre.focus({preventScroll: true}); }
      });
    }

    /* ------------------------------------------------------------------
       Étape 2 — le moment
       ------------------------------------------------------------------ */
    let zonesListe = null;
    function zonesDisponibles() {
      if (zonesListe) return zonesListe;
      let z = [];
      try { if (Intl.supportedValuesOf) z = Intl.supportedValuesOf('timeZone'); } catch { z = []; }
      if (!z.length) z = ['Europe/Paris', 'Europe/London', 'Europe/Brussels', 'Europe/Zurich', 'America/New_York', 'America/Montreal', 'America/Los_Angeles', 'Asia/Tokyo', 'Australia/Sydney', 'Indian/Reunion', 'America/Guadeloupe', 'Pacific/Tahiti'];
      return (zonesListe = z.filter(x => T().zoneValide(x)));
    }
    function remplirFuseaux() {
      const sel = $('[data-ciel-zone]'), dec = $('[data-ciel-decalage]');
      if (!sel || !T()) return;
      const courant = etat.moment.zone || '', region = /\//.test(courant) ? courant.split('/')[0] + '/' : '';
      const zones = zonesDisponibles().filter(z => !region || z.startsWith(region) || z === courant);
      if (courant && !zones.includes(courant) && !T().zoneValide(courant)) zones.unshift(courant);
      const opts = ['<option value="">' + echapper(t('— Choisir —', '— Choose —')) + '</option>'].concat(zones.map(z => '<option value="' + echapper(z) + '"' + (z === courant ? ' selected' : '') + '>' + echapper(z.replace(/_/g, ' ')) + '</option>'));
      const html = opts.join('');
      if (sel.dataset.html !== html) { sel.innerHTML = html; sel.dataset.html = html; }
      if (!dec.options.length) {
        const out = ['<option value="">' + echapper(t('— Choisir —', '— Choose —')) + '</option>'];
        for (let q = -48; q <= 56; q++) { const mn = Math.abs(q) * 15, s = (q < 0 ? '-' : '+') + String(Math.floor(mn / 60)).padStart(2, '0') + ':' + String(mn % 60).padStart(2, '0'); out.push('<option value="UTC' + s + '">UTC' + s.replace('-', '−') + '</option>'); }
        dec.innerHTML = out.join('');
      }
      dec.value = /^UTC[+-]\d{2}:\d{2}$/.test(courant) ? courant : '';
    }
    function majMoment() {
      const m = etat.moment;
      const d = $('[data-ciel-date]'), h = $('[data-ciel-heure]'), inc = $('[data-ciel-inconnue]');
      if (d.value !== m.date && document.activeElement !== d) d.value = m.date;
      if (h.value !== m.heure && document.activeElement !== h) h.value = m.heure;
      inc.checked = m.inconnue; h.disabled = m.inconnue;
      const bloc = $('[data-ciel-fuseau-bloc]'), ligne = $('[data-ciel-fuseau]'), choix = $('[data-ciel-fuseau-choix]'), bouton = $('[data-ciel-fuseau-modifier]');
      bloc.hidden = !etat.lieu;
      let texte = '';
      if (etat.lieu) {
        if (zoneEnCours && !m.zone) texte = t('Recherche du fuseau horaire du lieu…', 'Finding the time zone of the place…');
        else if (!m.zone) texte = t('Dans quel fuseau était-ce ? Choisissez-le ci-dessous.', 'Which time zone was it? Choose it below.');
        else if (ins) texte = T().libelle(ins.aff, nomVille(), loc);
        else texte = t('Fuseau : ', 'Time zone: ') + m.zone.replace(/_/g, ' ');
      }
      if (ligne.textContent !== texte) ligne.textContent = texte;
      const ouvert = fuseauOuvert || (!!etat.lieu && !m.zone && !zoneEnCours);
      choix.hidden = !ouvert; bouton.hidden = !m.zone; bouton.setAttribute('aria-expanded', String(ouvert));
      if (ouvert) remplirFuseaux();
      // Heure inexistante ou ambiguë : dans le flux, jamais un dialogue (cahier §10)
      const note = $('[data-ciel-note-heure]');
      let html = '';
      if (ins && T() && !m.inconnue) {
        const msg = T().message(ins.r, nomVille(), loc);
        if (msg && ins.r.statut === 'inexistante') {
          /* Si l'heure retenue tombe un AUTRE JOUR (jour sauté, changement d'heure à minuit), le bouton le dit :
             la date gravée change, on ne le devine jamais en silence (K11). */
          const p = ins.r.proposition, h2 = T().texteHeure(p.heure, loc);
          const quoi = p.date !== m.date
            ? (m.accepte ? t('✓ Le ciel du ', '✓ The sky of ') : t('Oui, le ciel du ', 'Yes, the sky of ')) + T().texteDate(p.date, loc) + t(' à ', ' at ') + h2
            : (m.accepte ? t('✓ Le ciel de ', '✓ The sky at ') : t('Oui, le ciel de ', 'Yes, the sky at ')) + h2;
          html = '<p>' + echapper(msg.texte) + '</p><button type="button" class="mtci-accepter" data-ciel-accepter aria-pressed="' + m.accepte + '">' + echapper(quoi) + '</button>';
        }
        else if (msg && ins.r.statut === 'ambigue') html = '<fieldset class="mtci-ambigue"><legend>' + echapper(msg.texte) + '</legend>' + msg.choix.map((c, i) => '<label><input type="radio" name="ciel-ambigue-' + (root.id || 'x') + '" value="' + (i + 1) + '" data-ciel-choix' + ((m.choix === 2 ? 1 : 0) === i ? ' checked' : '') + '><span>' + echapper(c.texte) + '</span></label>').join('') + '</fieldset>';
      }
      const notes = [];
      if (ins && sceneCiel && sceneCiel.jour && R()) notes.push(R().texte('jour', loc, {heure: T().texteHeure(ins.local.slice(11, 16), loc)}));
      if (ins && ins.local < '1970' && R()) notes.push(R().texte('avant1970', loc));
      html += notes.map(n => '<p class="mtci-note__info">' + echapper(n) + '</p>').join('');
      if (note.dataset.html !== html) { note.innerHTML = html; note.dataset.html = html; }
      const res = $('[data-ciel-resume-ciel]'), resume = sceneCiel ? ({jour: t('Ce jour-là au-dessus de vous : ', 'Above you that day: '), nuit: t('Cette nuit-là au-dessus de vous : ', 'Above you that night: ')}[quandCiel()] || t('Ce soir-là au-dessus de vous : ', 'Above you that night: ')) + sceneCiel.resume[loc] : '';
      if (res.textContent !== resume) res.textContent = resume;
      const alerte = $('[data-ciel-moment-alerte]'); alerte.textContent = erreurTemps || '';
      $('[data-ciel-suivant="1"]').disabled = !(ins && ins.pret);
    }

    /* ------------------------------------------------------------------
       Étapes 3 à 5 — format, visuel, mots
       ------------------------------------------------------------------ */
    function changerForme(f) {
      if (!PIECES[f] || f === etat.forme) return;
      etat.forme = f; root.dataset.shape = f;
      envoyerPont('state:change', {shape: etat.forme, hole: etat.trou});
      majTout(); peindre();
      if (pretAGraver()) graver();
      if (etape === 3) vignettes();
      sauver();
    }
    function changerTrou(oui) {
      etat.trou = !!oui; envoyerPont('state:change', {shape: etat.forme, hole: etat.trou});
      majTout(); peindre();
      if (pretAGraver()) graver();         // K20 : le trou de la tomette tombe dans le ciel, qui se redessine sans étoile coupée
      sauver();
    }
    function changerVisuel(v) {
      if (!VISUELS.includes(v) || v === etat.visuel) return;
      etat.visuel = v;
      majTout(); peindre();
      if (pretAGraver()) graver();
      sauver();
    }
    /* Les 3 vignettes : le ciel DU CLIENT, sur sa forme, au canvas (aucune gravure spéculative en V1, critique M6). */
    function vignettes() {
      if (!ins || !R() || !catalogue) return;
      const d = dpr(), {W, H} = dims();
      for (const v of VISUELS) {
        const c = $('[data-ciel-vignette="' + v + '"]'); if (!c) continue;
        const cw = Math.max(60, c.clientWidth || 100);
        c.width = Math.round(cw * d); c.height = Math.round(cw * H / W * d);
        try { R().apercu(c, params(null, v), {teinte: '#5b3a22', piece: true, fond: '#d8a27c'}); } catch (e) { console.warn('[ciel] vignette', e); }
      }
    }

    /* Vos mots */
    function majChamps() {
      const f = $('[data-ciel-date-format]'), lib = $('[data-ciel-date-libre]');
      f.value = etat.textes.fmtDate;
      lib.hidden = etat.textes.fmtDate !== 'libre';
      // les formats se montrent avec LE moment du client, sinon avec celui de l'exemple
      if (R()) {
        const local = ins ? ins.local : '2025-02-14T22:00', inconnue = ins && ins.heure === 'inconnue';
        for (const opt of f.options) {
          if (['libre', 'sans'].includes(opt.value)) continue;
          const fmt = inconnue ? (opt.value === 'points-heure' ? 'points' : opt.value === 'long-heure' ? 'long' : opt.value) : opt.value;
          const v = R().formats.instant(inconnue ? {date: local.slice(0, 10)} : local, fmt, loc) + (inconnue && /heure/.test(opt.value) ? t(' (sans heure)', ' (no time)') : '');
          if (opt.textContent !== v) opt.textContent = v;
        }
      }
      if (lib.value !== etat.textes.libre && document.activeElement !== lib) lib.value = etat.textes.libre;
      const lt = $('[data-ciel-lieu-texte]'); if (document.activeElement !== lt && lt.value !== etat.textes.lieuTexte) lt.value = etat.textes.lieuTexte;
      $('[data-ciel-coords-format]').value = etat.textes.fmtCoords;
      $('[data-ciel-coords-valeur]').textContent = texteCoords();
    }
    function apresTexte() { majResumes(); peindreBientot(40); if (pretAGraver()) graverBientot(500); verifierMots(); sauver(); }
    /* Les contrôles de gravabilité des mots : polices du gabarit (D35) et corps minimal (D10). */
    function problemesMots() {
      if (!R()) return [];
      const out = [], tx = textesGabarit();
      if (alerteLatin && !tx.lieu) out.push(alerteLatin + t(' : saisissez-le ci-dessous.', ': type it below.'));
      for (const [cle, v] of [['lieu', tx.lieu], ['date', tx.date]]) {
        if (v && !R().formats.gravable(v)) out.push(cle === 'lieu' ? t('Écrivez le nom du lieu en lettres latines : certains caractères ne peuvent pas être gravés.', 'Write the place name in Latin letters: some characters cannot be engraved.') : t('Votre texte contient des caractères qui ne peuvent pas être gravés.', 'Your text contains characters that cannot be engraved.'));
      }
      const {W} = dims();
      for (const e of elementsTextes()) {
        if (e.type !== 'text') continue;
        const m = e.__mise;
        if (m.boite.w > m.wFrac * W + 0.05) out.push((e._mtci === 'lieu' ? t('Le nom du lieu', 'The place name') : e._mtci === 'date' ? t('Votre texte', 'Your text') : t('Les coordonnées', 'The coordinates')) + t(' est trop long pour être gravé lisiblement : raccourcissez-le.', ' is too long to be engraved legibly: please shorten it.'));
      }
      return [...new Set(out)];
    }
    function verifierMots() { const p = problemesMots(); $('[data-ciel-mots-alerte]').textContent = p.join(' '); return p; }

    /* ------------------------------------------------------------------
       Les étapes : affichage, résumés, navigation
       ------------------------------------------------------------------ */
    const nomVisuel = v => ({medaillon: t('Le médaillon', 'The medallion'), 'medaillon-nuit': t('Le médaillon nuit', 'The night medallion'), pleine: etat.forme === 'carree' ? t('Plein carreau', 'Full-sky square tile') : t('Pleine tomette', 'Full-sky tile')}[v] || v);
    const nomForme = f => f === 'hexa' ? t('Tomette', 'Hexagonal tomette') : t('Carreau', 'Square tile');
    function texteQuand() {
      if (!ins) return '';
      const date = T().texteDate(ins.local.slice(0, 10), loc);
      return ins.heure === 'inconnue' ? date + t(' (heure inconnue)', ' (time unknown)') : date + ', ' + T().texteHeure(ins.local.slice(11, 16), loc);
    }
    function majResumes() {
      const res = [
        etat.lieu ? nomCourt() : '',
        texteQuand(),
        nomForme(etat.forme) + (etat.trou ? t(' percée', ' with hole') : '') + ' · ' + prixDe(etat.forme, etat.trou),
        nomVisuel(etat.visuel),
        [etat.textes.lieuTexte, texteDate()].filter(Boolean).join(' · '),
        prixDe(etat.forme, etat.trou)
      ];
      res.forEach((v, i) => { const p = $('[data-ciel-resume="' + i + '"]'); if (p) p.textContent = faites.has(i) || (i === 2 && etat.lieu) ? v : ''; });
      $('[data-ciel-recap]').textContent = [etat.textes.lieuTexte || nomCourt(), texteQuand(), nomVisuel(etat.visuel), nomForme(etat.forme) + (etat.trou ? t(' percée', ' with hole') : '')].filter(Boolean).join(' · ');
      const rc = $('[data-ciel-recap-ciel]'), rv = sceneCiel ? ({jour: t('Au-dessus de vous ce jour-là : ', 'Above you that day: '), nuit: t('Au-dessus de vous cette nuit-là : ', 'Above you that night: ')}[quandCiel()] || t('Au-dessus de vous ce soir-là : ', 'Above you that night: ')) + sceneCiel.resume[loc] : '';
      if (rc.textContent !== rv) rc.textContent = rv;
      for (const el of $$('[data-ciel-prix-pret]')) el.textContent = prixDe(etat.forme, etat.trou);
      const fp = $('[data-ciel-flottant-prix]'); if (fp && root.dataset.pret === 'true') fp.textContent = prixDe(etat.forme, etat.trou);
      const nomPleine = $('[data-ciel-nom-pleine]'); if (nomPleine) nomPleine.textContent = nomVisuel('pleine');
      const radioPleine = $('[data-ciel-visuel-pleine]'); if (radioPleine) radioPleine.setAttribute('aria-label', nomVisuel('pleine') + t(', le ciel jusqu’aux bords', ', the sky right to the edges'));
      const duo = $('[data-ciel-duo]'); if (duo) { const u = lienCarte(); if (u) duo.setAttribute('href', u); }
    }
    function majEtapes() {
      for (const s of $$('[data-ciel-etape]')) {
        const i = +s.dataset.cielEtape;
        s.dataset.etat = i === etape ? 'active' : faites.has(i) ? 'faite' : 'avenir';
        const corps = $('[data-ciel-corps="' + i + '"]'); if (corps) corps.hidden = i !== etape;
        const mod = s.querySelector('[data-ciel-aller]'); if (mod) mod.hidden = i === etape || !etat.lieu || !faites.has(i) || (i > 1 && !(ins && ins.pret));
      }
      root.dataset.etape = String(etape);
      const nomChoisi = $('[data-ciel-choisi-nom]'); if (nomChoisi) nomChoisi.textContent = etat.lieu ? '📍 ' + nomCourt() : '';
      $('[data-ciel-choisi]').hidden = !etat.lieu;
      $('[data-ciel-suivant="0"]').disabled = !etat.lieu;
      const pret = !!(cielAccorde() && etat.lieu && ins && ins.pret && (faites.has(4) || etape === 5));
      root.dataset.pret = String(pret);
      $('[data-ciel-ajout-page]').hidden = !pret;
      for (const r of $$('[data-ciel-forme]')) r.checked = r.value === etat.forme;
      $('[data-ciel-trou]').checked = etat.trou;
      for (const r of $$('[data-ciel-visuel]')) r.checked = r.value === etat.visuel;
      const ca = $('[data-ciel-cadeau]'); if (ca) { ca.checked = etat.cadeau.on; $('[data-ciel-cadeau-bloc]').hidden = !etat.cadeau.on; const m = $('[data-ciel-cadeau-msg]'); if (m.value !== etat.cadeau.msg) m.value = etat.cadeau.msg; }
      // Étape Prête : rappels et blocages de gravabilité
      const pa = $('[data-ciel-pret-alerte]');
      if (pa) {
        const p = problemesMots(), rappels = [...p];
        if (erreurCiel) rappels.unshift(erreurCiel);
        else if (etat.lieu && !(ins && ins.pret)) rappels.push(t('Il manque le moment : la date et l’heure, à l’étape « Moment ».', 'The moment is missing: the date and time, at the “Moment” step.'));
        pa.textContent = rappels.join(' ');
      }
      if (editionPanier) $('[data-ciel-etat-panier]').textContent = editionPanier;
      const qPret = $('.mt-carte-question--pret');
      if (qPret) { qPret.dataset.texte = qPret.dataset.texte || qPret.textContent; const v = ins && !cielAccorde() && !erreurCiel ? t('Votre ciel se grave…', 'Engraving your sky…') : qPret.dataset.texte; if (qPret.textContent !== v) qPret.textContent = v; }
    }
    function majTout() { majEtapes(); majMoment(); majResumes(); majChamps(); majAria(); majFlottant(); if (guide && guide.open) phaseGuide(etape, false); }
    function aller(n, {focus = true} = {}) {
      n = borne(n, 0, 5);
      if (n > 0 && !etat.lieu) return;
      if (n > 1 && !(ins && ins.pret)) n = 1;          // le moment d'abord : le format et le visuel en dépendent (K28)
      if (n > etape) for (let i = etape; i < n; i++) faites.add(i);
      else if (n < etape) faites.add(etape);
      etape = n;
      if (n >= 1) { chargerPont(); chargerCatalogue(); if (root.dataset.vue === 'photo') voirCreation(); }
      if (n === 5) prechaufferMoteur();
      majTout();
      if (n === 3) requestAnimationFrame(vignettes);
      if (guide && guide.open) phaseGuide(n, focus);
      else if (focus && !mobile.matches) {
        const s = $('[data-ciel-etape="' + n + '"]');
        if (s) {
          const r = s.getBoundingClientRect(); if (r.top < 80 || r.top > innerHeight * .6) s.scrollIntoView({block: 'start', behavior: reduit() ? 'auto' : 'smooth'});
          // Moment encore vide : le champ de la date directement ; sinon le titre de l'étape (jamais le <body>)
          if (n === 1 && !etat.moment.date) $('[data-ciel-date]')?.focus({preventScroll: true});
          else { s.querySelector('.mt-carte-etape__titre')?.setAttribute('tabindex', '-1'); s.querySelector('.mt-carte-etape__titre')?.focus({preventScroll: true}); }
        }
      }
      sauver();
    }

    /* ------------------------------------------------------------------
       Le guide plein écran (téléphone) — COPIE DE la fiche carte, sans « Précisez l'endroit » (V1.1)
       ------------------------------------------------------------------ */
    let guideMontages = [], guideFocus = null, guideBody = '', guideHtml = '';
    const TITRES = [t('Où étiez-vous ?', 'Where were you?'), t('Quand ?', 'When?'), t('Tomette ou carreau ?', 'Tomette or square tile?'), t('Votre ciel', 'Your sky'), t('Les mots de votre ciel', 'The words of your sky'), t('Il est prêt.', 'It’s ready.')];
    function guideViewport() {
      if (!guide || !guide.open) return;
      const v = window.visualViewport;
      guide.style.setProperty('--carte-guide-height', ((v && v.height) || innerHeight) + 'px');
      guide.style.setProperty('--carte-guide-top', ((v && v.offsetTop) || 0) + 'px');
      if (guide.dataset.mot) requestAnimationFrame(camera);
    }
    function phaseGuide(n, focus) {
      guide.dataset.etape = String(n);
      $('[data-ciel-guide-compte]').textContent = (n + 1) + ' / 6';
      $('[data-ciel-guide-titre]').textContent = n === 5 && ins && !cielAccorde() && !erreurCiel ? t('Votre ciel se grave…', 'Engraving your sky…') : TITRES[n];
      for (const b of $$('[data-ciel-guide-etape]')) {
        const i = +b.dataset.cielGuideEtape;
        if (i === n) b.setAttribute('aria-current', 'step'); else b.removeAttribute('aria-current');
        b.disabled = (i > 0 && !etat.lieu) || (i > 1 && !(ins && ins.pret));
      }
      const suivant = $('[data-ciel-guide-suivant]');
      suivant.hidden = n === 5;
      suivant.disabled = (n === 0 && !etat.lieu) || (n === 1 && !(ins && ins.pret));
      suivant.innerHTML = [t('C’est ici', 'This is the place'), t('Suivant', 'Next'), t('Suivant', 'Next'), t('Personnaliser mes mots', 'Personalise my words'), t('Voir mon ciel', 'See my sky')][n] + ' <span aria-hidden="true">→</span>';
      $('[data-ciel-guide-retour]').textContent = n ? t('← Retour', '← Back') : t('Fermer', 'Close');
      if (guide.dataset.phase !== String(n)) { guide.dataset.phase = String(n); $('[data-ciel-guide-contenu]').scrollTop = 0; }
      if (n !== 4 && guide.dataset.mot) finMots(false);
      if (focus) {
        if (n === 4) debutMots('date', true);
        else $('[data-ciel-guide-titre]').focus({preventScroll: true});
      }
      requestAnimationFrame(() => { guideViewport(); redimensionner(); });
    }
    function ouvrirGuide({focusRecherche = false} = {}) {
      if (!guide || !mobile.matches) {
        aller(etat.lieu ? etape : 0, {focus: !focusRecherche});
        $('[data-ciel-etapes]').scrollIntoView({block: 'start', behavior: reduit() ? 'auto' : 'smooth'});
        if (focusRecherche) champRecherche()?.focus({preventScroll: true});
        return;
      }
      if (!guide.open) {
        guideFocus = document.activeElement; guideBody = document.body.style.overflow; guideHtml = document.documentElement.style.overflow;
        const monter = (node, hote) => { const m = document.createComment('ciel-guide-position'); node.before(m); guideMontages.push({node, m}); hote.append(node); };
        monter($('[data-ciel-stage]'), $('[data-ciel-guide-apercu]'));
        for (let i = 0; i < 6; i++) monter($('[data-ciel-corps="' + i + '"]'), $('[data-ciel-guide-contenu]'));
        document.body.style.overflow = 'hidden'; document.documentElement.style.overflow = 'hidden';
        root.dataset.mobileGuide = 'true'; guide.showModal();
      }
      majTout(); phaseGuide(etape, !focusRecherche);
      // Dans le MÊME geste que l'appui : sinon le clavier iOS ne s'ouvre pas.
      if (focusRecherche && etape === 0) champRecherche()?.focus({preventScroll: true});
      guideViewport(); majFlottant();
    }
    function fermerGuide() {
      if (!guide || !guide.open) return;
      finMots(false);
      guide.close();
      for (const {node, m} of guideMontages) m.replaceWith(node);
      guideMontages = [];
      document.body.style.overflow = guideBody; document.documentElement.style.overflow = guideHtml;
      root.dataset.mobileGuide = 'false';
      piece.style.transform = '';
      majTout(); sauver();
      requestAnimationFrame(() => { redimensionner(); if (guideFocus && guideFocus.isConnected) guideFocus.focus({preventScroll: true}); });
    }
    const champRecherche = () => $('[data-ciel-recherche] input');

    /* Vos mots, un champ par écran ; la caméra zoome sur la ligne (COPIE DE la fiche carte) */
    const MOTS = ['date', 'lieu', 'coords'];
    function debutMots(champ, focus) {
      if (!guide || !guide.open || etape !== 4) return;
      guide.dataset.mot = champ;
      for (const b of $$('[data-ciel-mot]')) { if (b.dataset.cielMot === champ) b.setAttribute('aria-current', 'step'); else b.removeAttribute('aria-current'); }
      $('[data-ciel-mot-suivant]').textContent = champ === 'coords' ? t('Terminer →', 'Done →') : t('Suivant →', 'Next →');
      if (focus) {
        const cible = champ === 'date' ? (etat.textes.fmtDate === 'libre' ? $('[data-ciel-date-libre]') : $('[data-ciel-date-format]')) : champ === 'lieu' ? $('[data-ciel-lieu-texte]') : $('[data-ciel-coords-format]');
        cible.focus({preventScroll: true});
      }
      guideViewport(); requestAnimationFrame(camera);
    }
    function finMots(retour = true) {
      if (!guide || !guide.dataset.mot) return;
      delete guide.dataset.mot; piece.style.transform = '';
      document.activeElement && document.activeElement.blur && guide.contains(document.activeElement) && document.activeElement.blur();
      if (retour) { phaseGuide(4, false); $('[data-ciel-guide-titre]').focus({preventScroll: true}); }
      requestAnimationFrame(() => { guideViewport(); redimensionner(); });
    }
    function motSuivant() {
      const i = MOTS.indexOf(guide.dataset.mot);
      if (i >= 0 && i < MOTS.length - 1) debutMots(MOTS[i + 1], true);
      else { finMots(false); aller(5); }
    }
    function camera() {
      if (!guide || !guide.open || !guide.dataset.mot || !geo || !R()) return;
      const champ = guide.dataset.mot, el = elementsTextes().find(e => e._mtci === champ), hote = $('[data-ciel-guide-apercu]');
      piece.style.transform = 'none';
      if (!el) return;
      const {W, H} = dims(), b = el.__mise.boite, f = geo.face;
      const px = f.x + (b.x + b.w / 2) * f.width / W, py = f.y + (b.y + b.h / 2) * f.height / H, encre = b.w * f.width / W;
      const sr = piece.getBoundingClientRect(), hr = hote.getBoundingClientRect();
      if (!hr.width || !hr.height) return;
      const zoom = borne((hr.width - 38) / Math.max(100, encre), 1, 3.1);
      const x = hr.width / 2 - (sr.left - hr.left) - zoom * px, y = hr.height * .54 - (sr.top - hr.top) - zoom * py;
      piece.style.transform = 'translate(' + x + 'px,' + y + 'px) scale(' + zoom + ')';
    }

    /* Le bouton flottant (IntersectionObserver), comme la carte */
    function majFlottant() {
      const ancre = $('[data-ciel-ouvrir]'), flottant = $('[data-ciel-flottant]');
      if (!ancre || !flottant) return;
      const r = ancre.getBoundingClientRect(), masque = !!document.querySelector('dialog[open]');
      flottant.hidden = masque || !mobile.matches || r.width === 0 || (r.bottom > 0 && r.top < innerHeight);
      const fp = $('[data-ciel-flottant-prix]');
      if (fp && root.dataset.pret === 'true') fp.textContent = prixDe(etat.forme, etat.trou);
    }

    /* ------------------------------------------------------------------
       Le panier, par le pont (mt-ciel-produit.js)
       ------------------------------------------------------------------ */
    let pont = null, pontSession = null, pontPret = null, demandeEnCours = null, derniereDemande = null;
    function envoyerPont(type, data = {}) {
      if (!pontSession && type !== 'ready') return;
      root.dispatchEvent(new CustomEvent('mt-ciel:commerce', {detail: {channel: CHANNEL, type, session: pontSession, ...data}}));
    }
    function chargerPont() {
      if (pont) return pont;
      pont = new Promise((resolve, reject) => {
        let fini = false;
        pontPret = () => { if (!fini) { fini = true; resolve(true); } };
        const s = document.createElement('script');
        s.src = config.assets.produit; s.defer = true;
        s.onerror = () => { fini = true; pont = null; reject(Error(t('Le panier ne répond pas. Réessayez dans un instant.', 'The basket is not responding. Please try again in a moment.'))); };
        document.head.appendChild(s);
        setTimeout(() => { if (!fini) { fini = true; pont = null; reject(Error(t('Le panier tarde à répondre. Réessayez dans un instant.', 'The basket is slow to respond. Please try again in a moment.'))); } }, 20000);
      });
      pont.catch(() => {});
      return pont;
    }
    function prechaufferMoteur() {
      chargerPont().then(() => {
        const go = () => envoyerPont('engine:warm');
        if (window.requestIdleCallback) requestIdleCallback(go, {timeout: 3000}); else setTimeout(go, 1200);
      }).catch(() => {});
    }
    root.addEventListener('mt-ciel:studio', ev => {
      const m = ev.detail;
      if (!m || m.channel !== CHANNEL) return;
      if (m.type === 'init') {
        const premiere = !pontSession;
        pontSession = m.session;
        if (premiere) envoyerPont('ready', {requestId: uuid().replace(/[^A-Za-z0-9_-]/g, '')});
        if (pontPret) pontPret();
        return;
      }
      if (m.session !== pontSession) return;
      if (m.type === 'edit:result') {
        editionPanier = m.ok ? t('Vous modifiez le ciel de votre panier : l’ajout remplacera l’ancienne version.', 'You are editing the sky in your basket: adding it will replace the previous version.') : '';
        if (m.ok && m.cadeau && m.cadeau.on === true) etat.cadeau = {on: true, msg: typeof m.cadeau.msg === 'string' ? m.cadeau.msg.slice(0, 200) : ''};
        majEtapes();
      } else if (m.type === 'cart:progress') {
        const textes = {prepare: t('Vérification de votre ciel…', 'Checking your sky…'), render: t('Préparation du fichier de gravure…', 'Preparing the engraving file…'), save: t('Enregistrement de votre ciel…', 'Saving your sky…'), cart: t('Ajout au panier…', 'Adding to your basket…'), verify: t('Vérification du panier…', 'Checking your basket…')};
        messagePanier(textes[m.stage] || '');
      } else if (m.type === 'cart:result') {
        if (!demandeEnCours || m.requestId !== demandeEnCours.id) return;
        if (m.ok) { messagePanier(t('Votre ciel est dans le panier.', 'Your sky is in your basket.')); try { localStorage.removeItem(cleBrouillon); } catch { /* rien */ } }
        else { messagePanier(m.error || t('L’ajout n’a pas abouti.', 'The item could not be added.'), true); $('[data-ciel-panier-actions]').hidden = false; demandeEnCours.echec = true; }
      }
    });
    const dialoguePanier = $('[data-ciel-panier]');
    function messagePanier(texte, erreur = false) {
      const m = $('[data-ciel-panier-message]'); m.textContent = texte; m.dataset.error = String(erreur);
      const e = $('[data-ciel-etat-panier]'); e.textContent = erreur ? texte : ''; e.dataset.error = String(erreur);
    }
    function verifierAvantPanier() {
      if (!etat.lieu) return t('Choisissez d’abord votre lieu.', 'Choose your place first.');
      if (!(ins && ins.pret)) return erreurTemps || t('Il manque le moment : la date et l’heure, à l’étape « Moment ».', 'The moment is missing: the date and time, at the “Moment” step.');
      if (!ciel) return erreurCiel || t('Votre ciel n’a pas pu être préparé.', 'Your sky could not be prepared.');
      if (!cielAccorde()) return t('Votre ciel se redessine pour cette forme. Réessayez dans un instant.', 'Your sky is being redrawn for this shape. Please try again in a moment.');
      const p = problemesMots(); if (p.length) return p[0];
      return '';
    }
    /* Le ciel se grave encore (lien rouvert, « Modifier » depuis le panier, autre forme) : on l'attend. */
    async function attendreCiel() {
      const fin = Date.now() + 90000;
      while (etat.lieu && ins && ins.pret && (!cielAccorde() || graverMinuteur || gravureEnCours) && !erreurCiel && Date.now() < fin && (gravureEnCours || graverMinuteur || zoneEnCours)) {
        await Promise.race([gravureEnCours || new Promise(r => setTimeout(r, 300)), new Promise(r => setTimeout(r, 1000))]).catch(() => {});
      }
    }
    async function ajouter() {
      if (etat.lieu && ins && ins.pret && !erreurCiel && (gravureEnCours || graverMinuteur || zoneEnCours || !cielAccorde())) {
        if (!dialoguePanier.open) dialoguePanier.showModal();
        $('[data-ciel-panier-actions]').hidden = true; messagePanier(t('Votre ciel se grave…', 'Engraving your sky…'));
        if (!cielAccorde() && !gravureEnCours && !graverMinuteur) graver();
        await attendreCiel();
        if (!cielAccorde()) dialoguePanier.close();
      }
      const probleme = verifierAvantPanier();
      if (probleme) { if (etape !== 5) aller(5); const pa = $('[data-ciel-pret-alerte]'); pa.textContent = probleme; dire(probleme, true); return; }
      try {
        if (!dialoguePanier.open) dialoguePanier.showModal();
        $('[data-ciel-panier-actions]').hidden = true; messagePanier(t('Vérification de votre ciel…', 'Checking your sky…'));
        if (gravureEnCours) await gravureEnCours;
        // la dernière saisie est gravée avant de partir : les marques data-mtci-textes doivent dire ce qui est écrit
        if (graverMinuteur || (ciel && ciel.textes !== JSON.stringify(textesGabarit()))) { clearTimeout(graverMinuteur); graverMinuteur = 0; await graver(); }
        if (!ciel || !cielAccorde()) throw Error(erreurCiel || t('Votre ciel n’a pas pu être préparé.', 'Your sky could not be prepared.'));
        await chargerPont();
        if (!pontSession) throw Error(t('Le panier ne répond pas. Réessayez dans un instant.', 'The basket is not responding. Please try again in a moment.'));
        const rec = recette(), cle = JSON.stringify([rec, etat.cadeau]);
        // Une même composition rejouée après un échec garde son identifiant : le pont ne crée jamais de doublon.
        const id = derniereDemande && derniereDemande.cle === cle ? derniereDemande.id : uuid().replace(/[^A-Za-z0-9_-]/g, '');
        derniereDemande = {cle, id}; demandeEnCours = {id};
        envoyerPont('cart:add', {requestId: id, recipe: rec, cadeau: etat.cadeau.on && $('[data-ciel-cadeau]') ? {on: true, msg: etat.cadeau.msg} : null /* F1 16/10 : sans case à l'écran, jamais d'emballage (brouillon ou panier anciens compris) */, etat: encoder()});
      } catch (err) {
        messagePanier(err.message, true); $('[data-ciel-panier-actions]').hidden = false;
      }
    }

    /* ------------------------------------------------------------------
       Branchements
       ------------------------------------------------------------------ */
    root.addEventListener('click', ev => {
      const b = ev.target.closest('button,[data-ciel-photo]');
      if (!b || !root.contains(b) && !(guide && guide.contains(b))) return;
      if (b.matches('[data-ciel-suivant]')) aller(+b.dataset.cielSuivant + 1);
      else if (b.matches('[data-ciel-aller]')) aller(+b.dataset.cielAller);
      else if (b.matches('[data-ciel-changer-lieu]')) { $('[data-ciel-choisi]').hidden = true; const c = champRecherche(); if (c) { c.value = ''; c.focus(); } }
      else if (b.matches('[data-ciel-fuseau-modifier]')) { fuseauOuvert = !fuseauOuvert; majMoment(); if (fuseauOuvert) $('[data-ciel-zone]')?.focus(); }
      else if (b.matches('[data-ciel-accepter]')) { etat.moment.accepte = !etat.moment.accepte; apresMoment(0); }
      else if (b.matches('[data-ciel-ouvrir],[data-ciel-flottant-ouvrir]')) {
        if (b.matches('[data-ciel-flottant-ouvrir]') && root.dataset.pret === 'true') { ajouter(); return; }
        ouvrirGuide({focusRecherche: !etat.lieu});
      }
      else if (b.matches('[data-ciel-ajouter],[data-ciel-ajout-page]')) ajouter();
      else if (b.matches('[data-ciel-copier]')) copierLien(b);
      else if (b.matches('[data-ciel-guide-fermer]')) fermerGuide();
      else if (b.matches('[data-ciel-guide-retour]')) { if (etape === 0) fermerGuide(); else aller(etape - 1); }
      else if (b.matches('[data-ciel-guide-suivant]')) aller(etape + 1);
      else if (b.matches('[data-ciel-guide-etape]')) aller(+b.dataset.cielGuideEtape);
      else if (b.matches('[data-ciel-mot]')) debutMots(b.dataset.cielMot, true);
      else if (b.matches('[data-ciel-mot-suivant]')) motSuivant();
      else if (b.matches('[data-ciel-mots-fin]')) finMots(true);
      else if (b.matches('[data-ciel-panier-reessayer]')) { erreurCiel = ''; ajouter(); }
      else if (b.matches('[data-ciel-panier-fermer]')) dialoguePanier.close();
      else if (b.matches('[data-ciel-photo]')) voirPhoto(b);
      else if (b.matches('[data-ciel-creation]')) voirCreation();
    });
    root.addEventListener('change', ev => {
      const x = ev.target;
      if (x.matches('[data-ciel-forme]')) changerForme(x.value);
      else if (x.matches('[data-ciel-trou]')) changerTrou(x.checked);
      else if (x.matches('[data-ciel-visuel]')) changerVisuel(x.value);
      else if (x.matches('[data-ciel-inconnue]')) { etat.moment.inconnue = x.checked; etat.moment.accepte = false; apresMoment(0); }
      else if (x.matches('[data-ciel-choix]')) { etat.moment.choix = x.value === '2' ? 2 : 1; apresMoment(0); }
      else if (x.matches('[data-ciel-zone]')) { if (x.value && T().zoneValide(x.value)) { etat.moment.zone = x.value; etat.moment.zoneSource = 'client'; etat.moment.accepte = false; apresMoment(0); } }
      else if (x.matches('[data-ciel-decalage]')) { if (x.value && T().zoneValide(x.value)) { etat.moment.zone = x.value; etat.moment.zoneSource = 'client'; etat.moment.accepte = false; apresMoment(0); } }
      else if (x.matches('[data-ciel-date-format]')) { etat.textes.fmtDate = DATES.includes(x.value) ? x.value : 'points-heure'; majChamps(); apresTexte(); if (guide && guide.dataset.mot === 'date' && etat.textes.fmtDate === 'libre') $('[data-ciel-date-libre]').focus(); }
      else if (x.matches('[data-ciel-coords-format]')) { etat.textes.fmtCoords = COORDS.includes(x.value) ? x.value : 'dms'; majChamps(); apresTexte(); }
      else if (x.matches('[data-ciel-cadeau]')) { etat.cadeau.on = x.checked; majEtapes(); sauver(); }
    });
    root.addEventListener('input', ev => {
      const x = ev.target;
      if (x.matches('[data-ciel-date]')) { const v = /^\d{4}-\d{2}-\d{2}$/.test(x.value) ? x.value : ''; if (v !== etat.moment.date) { etat.moment.date = v; etat.moment.accepte = false; etat.moment.choix = 1; apresMoment(); } }
      else if (x.matches('[data-ciel-heure]')) { const v = /^\d{2}:\d{2}/.test(x.value) ? x.value.slice(0, 5) : ''; if (v !== etat.moment.heure) { etat.moment.heure = v; etat.moment.accepte = false; etat.moment.choix = 1; apresMoment(); } }
      else if (x.matches('[data-ciel-date-libre]')) { etat.textes.libre = x.value.slice(0, 40); apresTexte(); }
      else if (x.matches('[data-ciel-lieu-texte]')) { etat.textes.lieuTexte = x.value.toLocaleUpperCase(loc).slice(0, 32); etat.textes.lieuAuto = false; alerteLatin = ''; apresTexte(); }
      else if (x.matches('[data-ciel-cadeau-msg]')) { etat.cadeau.msg = x.value.slice(0, 200); sauver(); }
      if (guide && guide.dataset.mot) requestAnimationFrame(camera);
    });
    root.addEventListener('keydown', ev => {
      if (ev.key === 'Enter' && guide && guide.dataset.mot && ev.target.matches('input')) { ev.preventDefault(); motSuivant(); }
    });
    if (guide) {
      guide.addEventListener('cancel', ev => { ev.preventDefault(); if (guide.dataset.mot) finMots(true); else fermerGuide(); });
      guide.addEventListener('close', () => { if (root.dataset.mobileGuide === 'true') fermerGuide(); });
    }
    if (dialoguePanier) dialoguePanier.addEventListener('cancel', ev => { if ($('[data-ciel-panier-actions]').hidden) ev.preventDefault(); });
    async function copierLien(b) {
      const url = lienCiel(), texte0 = b.textContent;
      let ok = false;
      try { await navigator.clipboard.writeText(url); ok = true; } catch { /* repli ci-dessous */ }
      if (!ok) { try { const ta = document.createElement('textarea'); ta.value = url; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0'; document.body.append(ta); ta.select(); ok = document.execCommand('copy'); ta.remove(); } catch { ok = false; } }
      b.textContent = ok ? t('Lien copié ✓', 'Link copied ✓') : url;
      setTimeout(() => { b.textContent = texte0; }, 2600);
    }
    function voirPhoto(b) {
      const im = $('.mt-carte-media'); if (!im) return;
      im.removeAttribute('srcset'); im.src = b.dataset.src; im.alt = b.dataset.alt || '';
      root.dataset.vue = 'photo';
      for (const v of $$('.mt-carte-vignette')) v.setAttribute('aria-pressed', String(v === b));
    }
    function voirCreation() {
      root.dataset.vue = 'ciel';
      for (const v of $$('.mt-carte-vignette')) v.setAttribute('aria-pressed', String(v.matches('[data-ciel-creation]')));
    }
    let redimMinuteur = 0, derniereTaille = '';
    function redimensionner() {
      clearTimeout(redimMinuteur);
      redimMinuteur = setTimeout(() => {
        const {w, h} = taillePiece(), k = w + 'x' + h;
        if (k === derniereTaille && root.dataset.peint === 'true') { camera(); return; }
        derniereTaille = k; peindre();
        if (etape === 3) vignettes();
      }, 90);
    }
    if (typeof ResizeObserver !== 'undefined') new ResizeObserver(redimensionner).observe(scene);
    window.addEventListener('resize', () => { guideViewport(); redimensionner(); majFlottant(); });
    if (window.visualViewport) { visualViewport.addEventListener('resize', guideViewport); visualViewport.addEventListener('scroll', guideViewport); }
    if (typeof IntersectionObserver !== 'undefined' && $('[data-ciel-ouvrir]')) new IntersectionObserver(() => majFlottant(), {threshold: [0, 1]}).observe($('[data-ciel-ouvrir]'));
    window.addEventListener('scroll', () => requestAnimationFrame(majFlottant), {passive: true});
    mobile.addEventListener?.('change', () => { if (!mobile.matches && guide && guide.open) fermerGuide(); majFlottant(); redimensionner(); });

    // Ordinateur : l'aperçu collant se cale SOUS l'en-tête collant du site (COPIE DE la fiche carte).
    (function () {
      const mq = matchMedia('(min-width:881px)'); let f = 0, dernier = -1;
      function calc() {
        f = 0; let b = 0;
        if (mq.matches) document.querySelectorAll('.shopify-section-group-header-group,sticky-header,header').forEach(e => { const s = getComputedStyle(e); if ((s.position !== 'sticky' && s.position !== 'fixed') || s.visibility === 'hidden' || s.display === 'none') return; const x = e.getBoundingClientRect(); if (x.top <= 1 && x.bottom > b && x.height < 300 && x.width > innerWidth / 2) b = x.bottom; });
        b = Math.max(0, Math.round(b)); if (b !== dernier) { dernier = b; root.style.setProperty('--mtx-ph-top', b + 'px'); }
      }
      const planifier = () => { if (!f) f = requestAnimationFrame(calc); };
      addEventListener('scroll', planifier, {passive: true}); addEventListener('resize', planifier); calc();
    })();

    /* ------------------------------------------------------------------
       Démarrage
       ------------------------------------------------------------------ */
    if (!R() || !T() || !L() || !peintre) {
      dire(t('La création de votre ciel est momentanément indisponible. Réessayez dans quelques minutes.', 'Creating your sky is temporarily unavailable. Please try again in a few minutes.'), true);
      root.removeAttribute('aria-busy');
      return;
    }
    R().config({assets: {rendu: config.assets.rendu, worker: config.assets.worker, clipper: config.assets.clipper, catalogue: config.assets.catalogue, fuseaux: config.assets.fuseaux}, locale: config.locale});
    root.dataset.lieu = 'false'; root.dataset.vue = 'ciel'; root.dataset.peint = 'false';
    window.CartePeintre.polices().catch(() => {}).then(() => {
      // Reprise : lien #ciel= (prioritaire, « Modifier » depuis le panier compris), sinon brouillon de cet appareil.
      const hash = new URLSearchParams(location.hash.slice(1));
      const lien = hash.get('ciel');
      let repris = null, etapeReprise = 5;
      if (lien) repris = decoder(lien);
      if (lien && !repris) dire(t('Ce lien de ciel est incomplet : recommencez à partir de votre lieu.', 'This sky link is incomplete: start again from your place.'), true);
      if (!repris) { const b = lireBrouillon(); if (b) { repris = b.etat; etapeReprise = b.etape; } }
      if (repris) {
        etat = repris; root.dataset.shape = etat.forme; root.dataset.lieu = 'true'; root.dataset.cree = 'true';
        calculerInstant();
        if (!etat.moment.zone) trouverZone();
        if (!(ins && ins.pret) && etapeReprise > 1) etapeReprise = 1;
        for (let i = 0; i < etapeReprise; i++) faites.add(i);
        etape = etapeReprise;
        if (!etat.textes.lieuTexte && etat.textes.lieuAuto) etiqueter(etat.lieu);
        R().prechauffer();
        chargerCatalogue().then(() => { if (pretAGraver()) graver(); if (etape === 3) vignettes(); });
      }
      if (hash.get('k') || lien) chargerPont().catch(() => {});
      monterLieu();
      majTout(); peindre();
      root.removeAttribute('aria-busy');
    });
  }
  function demarrer() { document.querySelectorAll('[data-ciel-product]').forEach(mount); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', demarrer); else demarrer();
  document.addEventListener('shopify:section:load', ev => ev.target.querySelectorAll('[data-ciel-product]').forEach(mount));
})();
